/**
 * validateAiCode.ts – AST-basierter Sicherheitsvalidator für AI-generierten Code
 *
 * ── Bedrohungsmodell ──────────────────────────────────────────────────────────
 *
 * Die AI generiert JavaScript-Code, der über `evaluate()` im Browser ausgeführt
 * wird. Der Code läuft in einem `with (apiObject) { ... }` Kontext, sodass die
 * API-Funktionen als globale Variablen verfügbar sind. Das `with`-Statement
 * dient der Ergonomie, NICHT der Sicherheit.
 *
 * Sicherheit wird ausschließlich durch diesen AST-Validator gewährleistet.
 * Er prüft den Code VOR der Ausführung auf bekannte Escape-Vektoren:
 *
 *   1. `this`         → `window` in non-strict mode  (ThisExpression)
 *   2. `arguments`    → callee.constructor chain      (Identifier)
 *   3. `.constructor`  → Function('return this')()    (MemberExpression)
 *   4. `import()`      → dynamischer Module-Import    (ImportExpression)
 *   5. Globale Objekte → window, document, APP, etc.  (Identifier)
 *   6. Computed access → obj['constr'+'uctor']        (komplexe MemberExpression)
 *
 * ── Design-Entscheidungen ─────────────────────────────────────────────────────
 *
 * • Blocklist statt Allowlist auf Knoten-Ebene: Der AI-Code darf normales JS
 *   verwenden (Loops, Conditionals, Arrow-Functions, etc.). Nur spezifische
 *   gefährliche Konstrukte werden blockiert.
 *
 * • Acorn als Parser: ~35kB, zero Dependencies, parst ~10kB in <1ms.
 *   Bereits als transitive Webpack-Dependency vorhanden.
 *
 * • Fehlermeldungen sind so formuliert, dass die AI sie versteht und
 *   korrigierten Code senden kann.
 *
 * • Computed MemberExpression: nur numerische Literale und einfache Identifier
 *   als Key erlaubt. String-Literale werden gegen BLOCKED_PROPERTIES geprüft.
 *   Alle komplexeren Ausdrücke (BinaryExpression, CallExpression, etc.) werden
 *   blockiert, da sie zur Obfuscation genutzt werden könnten.
 */
import * as acorn from "acorn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ValidationResult {
  /** Ob der Code sicher ist und ausgeführt werden darf */
  safe: boolean;
  /** Menschenlesbare Fehlermeldung (wird an die AI zurückgegeben) */
  reason?: string;
  /** Position des Verstoßes im Original-Code (1-based line, 0-based column) */
  loc?: { line: number; column: number };
}

// ── Blocklisten ───────────────────────────────────────────────────────────────

/**
 * AST-Knotentypen, die pauschal verboten sind.
 */
const BLOCKED_NODE_TYPES = new Set([
  "ThisExpression",          // Escape: this → window in sloppy mode
  "ImportExpression",        // Escape: dynamic import()
  "MetaProperty",            // import.meta
  "WithStatement",           // Kein verschachteltes with (das äußere kommt von Evaluate.ts)
  "TaggedTemplateExpression", // Unnötig, potenzielle Angriffsfläche (Tag-Function-Zugriff)
]);

/**
 * Identifier-Namen, die nie als Variablenreferenz auftreten dürfen.
 * (Werden NICHT geprüft, wenn sie als statischer Property-Name in dot-notation
 * verwendet werden – das wird separat über BLOCKED_PROPERTIES abgedeckt.)
 */
const BLOCKED_IDENTIFIERS = new Set([
  // Scope-Escape
  "arguments",
  "eval",
  "Function",
  "AsyncFunction",
  "GeneratorFunction",
  "constructor",          // auch als Variable blockiert, nicht nur als Property

  // Browser-Globals
  "globalThis",
  "window",
  "self",
  "document",
  "location",
  "navigator",

  // Netzwerk
  "fetch",
  "XMLHttpRequest",
  "WebSocket",
  "EventSource",

  // Storage
  "localStorage",
  "sessionStorage",
  "indexedDB",
  "caches",

  // Frame/Window-Zugriff
  "postMessage",
  "opener",
  "parent",
  "top",
  "frames",

  // Meta-Programmierung
  "Proxy",
  "Reflect",

  // Anwendungsspezifisch
  "APP",
  "require",

  // Native Dialoge
  "alert",
  "confirm",
  "prompt",
]);

/**
 * Property-Namen, die in MemberExpression (dot-notation und bracket-notation)
 * verboten sind. Dies ist der Hauptschutz gegen Constructor-Chain-Escapes.
 */
const BLOCKED_PROPERTIES = new Set([
  "constructor",
  "__proto__",
  "prototype",
  "__defineGetter__",
  "__defineSetter__",
  "__lookupGetter__",
  "__lookupSetter__",
]);

/** Maximale Code-Länge (DoS-Schutz) */
const MAX_CODE_LENGTH = 10_000;

// ── Validator ─────────────────────────────────────────────────────────────────

/**
 * Validiert AI-generierten JavaScript-Code auf Sicherheit.
 *
 * @param jsCode - Der rohe Code-String aus dem AI evaluate-Tool
 * @returns ValidationResult mit `safe: true` oder `safe: false` + Grund
 */
export function validateAiCode(jsCode: string): ValidationResult {
  // ── Längenprüfung ──────────────────────────────────────────────────────
  if (jsCode.length > MAX_CODE_LENGTH) {
    return {
      safe: false,
      reason: `Code ist zu lang (${jsCode.length} Zeichen, max ${MAX_CODE_LENGTH}). Halte den Code kurz und einfach.`,
    };
  }

  // ── Parsing ────────────────────────────────────────────────────────────
  // Wrap in async function, damit await und return im Code erlaubt sind.
  // Das Offset (34 Zeichen) muss bei Positionsangaben abgezogen werden.
  const wrapper = `(async function __sandbox__() { ${jsCode} })`;
  const WRAPPER_OFFSET = "(async function __sandbox__() { ".length;

  let ast: acorn.Node;
  try {
    ast = acorn.parse(wrapper, {
      ecmaVersion: 2022,
      sourceType: "script",
      locations: true,      // für Fehlerpositionen
    });
  } catch (e: any) {
    // Syntax-Fehler – Position korrigieren
    const msg = e.message || String(e);
    return {
      safe: false,
      reason: `Syntaxfehler im Code: ${msg}`,
    };
  }

  // ── AST-Traversal ──────────────────────────────────────────────────────
  const violation = visitNode(ast, null, WRAPPER_OFFSET);
  if (violation) return violation;

  return { safe: true };
}

// ── AST-Visitor ───────────────────────────────────────────────────────────────

/**
 * Rekursiver AST-Visitor. Gibt bei einem Verstoß ein ValidationResult zurück,
 * sonst null.
 */
function visitNode(
  node: any,
  parent: any,
  wrapperOffset: number
): ValidationResult | null {
  if (!node || typeof node !== "object" || !node.type) return null;

  // ── Blocked Node Types ─────────────────────────────────────────────────
  if (BLOCKED_NODE_TYPES.has(node.type)) {
    return fail(
      `"${node.type}" ist im AI-Code nicht erlaubt.` +
        (node.type === "ThisExpression"
          ? " Verwende stattdessen die API-Funktionen direkt."
          : ""),
      node,
      wrapperOffset
    );
  }

  // ── Identifier-Prüfung ────────────────────────────────────────────────
  if (node.type === "Identifier" && BLOCKED_IDENTIFIERS.has(node.name)) {
    // Nur prüfen wenn der Identifier als Variablenreferenz verwendet wird,
    // NICHT als statischer Property-Name in dot-notation (obj.constructor).
    // Property-Namen werden separat über die MemberExpression-Prüfung abgedeckt.
    if (!isStaticPropertyOf(node, parent)) {
      return fail(
        `"${node.name}" ist im AI-Code nicht erlaubt. Verwende nur die bereitgestellten API-Funktionen.`,
        node,
        wrapperOffset
      );
    }
  }

  // ── MemberExpression-Prüfung ──────────────────────────────────────────
  if (node.type === "MemberExpression") {
    const result = checkMemberExpression(node, wrapperOffset);
    if (result) return result;
  }

  // ── Rekursion über alle Child-Nodes ───────────────────────────────────
  for (const key of Object.keys(node)) {
    if (key === "type" || key === "loc" || key === "start" || key === "end") continue;

    const child = node[key];
    if (Array.isArray(child)) {
      for (const item of child) {
        if (item && typeof item === "object" && item.type) {
          const result = visitNode(item, node, wrapperOffset);
          if (result) return result;
        }
      }
    } else if (child && typeof child === "object" && child.type) {
      const result = visitNode(child, node, wrapperOffset);
      if (result) return result;
    }
  }

  return null;
}

// ── MemberExpression-Prüfung ──────────────────────────────────────────────────

/**
 * Prüft einen MemberExpression-Knoten auf blockierte Property-Zugriffe.
 *
 * Statisch (obj.constructor):
 *   → Property-Name gegen BLOCKED_PROPERTIES prüfen
 *
 * Computed (obj[expr]):
 *   → Numerisches Literal: erlaubt        (teile[0])
 *   → Einfacher Identifier: erlaubt       (teile[i])
 *   → String-Literal: gegen BLOCKED_PROPERTIES prüfen  (obj['füllung'] OK, obj['constructor'] blocked)
 *   → Alles andere: blockiert             (obj['constr'+'uctor'], obj[fn()], ...)
 */
function checkMemberExpression(node: any, wrapperOffset: number): ValidationResult | null {
  if (!node.computed) {
    // Dot-notation: obj.property
    const propName = node.property?.name;
    if (propName && BLOCKED_PROPERTIES.has(propName)) {
      return fail(
        `Zugriff auf .${propName} ist nicht erlaubt.`,
        node.property,
        wrapperOffset
      );
    }
  } else {
    // Bracket-notation: obj[expr]
    const key = node.property;
    if (!key) return null;

    if (key.type === "Literal") {
      if (typeof key.value === "number") {
        // Numerisches Literal: obj[0], obj[1] → OK
        return null;
      }
      if (typeof key.value === "string") {
        // String-Literal: obj['füllung'] → prüfe gegen Blocklist
        if (BLOCKED_PROPERTIES.has(key.value)) {
          return fail(
            `Zugriff auf ["${key.value}"] ist nicht erlaubt.`,
            key,
            wrapperOffset
          );
        }
        // Erlaubtes String-Literal
        return null;
      }
    }

    if (key.type === "Identifier") {
      // Einfacher Identifier als Key: obj[i], teile[index] → OK
      // (Der Identifier selbst wird in der regulären Identifier-Prüfung gecheckt)
      return null;
    }

    // Alles andere: BinaryExpression, CallExpression, TemplateLiteral,
    // ConditionalExpression, etc. → blockiert (Obfuscation-Schutz)
    return fail(
      `Berechneter Property-Zugriff mit komplexem Ausdruck ist nicht erlaubt. ` +
        `Verwende einfache Identifier (obj[i]) oder Literale (obj[0], obj["key"]).`,
      key,
      wrapperOffset
    );
  }

  return null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Prüft ob ein Identifier-Node die statische Property eines MemberExpression
 * in dot-notation ist (z.B. das `foo` in `obj.foo`).
 * In dem Fall ist es kein Variablen-Lookup und wird separat geprüft.
 */
function isStaticPropertyOf(identifierNode: any, parentNode: any): boolean {
  return (
    parentNode?.type === "MemberExpression" &&
    !parentNode.computed &&
    parentNode.property === identifierNode
  );
}

/**
 * Erzeugt ein Fail-Result mit korrigierter Positionsangabe.
 */
function fail(reason: string, node: any, wrapperOffset: number): ValidationResult {
  const loc = node?.loc?.start;
  return {
    safe: false,
    reason,
    ...(loc
      ? {
          loc: {
            line: loc.line,
            // Spalte korrigieren wenn in der ersten Zeile (Wrapper-Offset)
            column: loc.line === 1 ? loc.column - wrapperOffset : loc.column,
          },
        }
      : {}),
  };
}

